/*
 * Copyright (c) 2021. FINN Sp. z o.o.
 */

package pl.finn.forms.utils.form

import io.kvision.core.Component
import io.kvision.core.Container
import io.kvision.form.FormControl
import io.kvision.form.FormHorizontalRatio
import io.kvision.form.FormType
import io.kvision.html.Div
import io.kvision.panel.SimplePanel
import kotlinx.serialization.KSerializer
import kotlinx.serialization.serializer
import pl.finn.forms.model.DocumentTreeNode
import kotlin.reflect.KProperty1

@Suppress("TooManyFunctions")
class FormBuilder<K : Any>(
  private val type: FormType? = null, horizRatio: FormHorizontalRatio = FormHorizontalRatio.RATIO_2,
  classes: String = "", serializer: KSerializer<K>? = null,
) : SimplePanel(classes) {

  val form = ExtendedForm(this, serializer)

  var horizRatio by refreshOnUpdate(horizRatio)

  private fun <C : FormControl> addInternal(
    key: DocumentTreeNode, control: C, required: Boolean = false, requiredMessage: String? = null,
    validatorMessage: ((C) -> String?)? = null, validator: ((C) -> Boolean?)? = null
  ): Component {
    when (type) {
      FormType.INLINE -> control.styleForInlineFormPanel()
      FormType.HORIZONTAL -> control.styleForHorizontalFormPanel(horizRatio)
      else -> control.styleForVerticalFormPanel()
    }
    if (required) control.flabel.addCssClass("required-label")
    form.addInternal(key, control, required, requiredMessage, validatorMessage, validator)
    return control
  }

  fun getElement(formElement: FormElement): Component {
    return addInternal(
      formElement.fieldValue,
      formElement.field,
      formElement.required,
      formElement.requiredMessage,
      formElement.validatorMessage,
      formElement.validator
    )
  }

  private val validationAlert = Div(className = "alert alert-danger").apply {
    role = "alert"
    visible = false
  }

  var validator
    get() = form.validator
    set(value) {
      form.validator = value
    }

  internal var validatorError: String?
    get() = validationAlert.content
    set(value) {
      validationAlert.content = value
      validationAlert.visible = value != null
      refresh()
    }

  fun getControl(key: KProperty1<K, *>): FormControl? {
    return form.getControl(key)
  }

  operator fun get(key: KProperty1<K, *>): Any? {
    return getControl(key)?.getValue()
  }

  fun getControlField(controlKey: DocumentTreeNode?): FormControl? {
    return form.getControlField(controlKey)
  }

  fun getData(): K {
    return form.getData()
  }

  fun validate(markFields: Boolean = true): Boolean {
    return singleRender {
      form.validate(markFields)
    }
  }

  companion object {
    inline fun <reified K : Any> create(
      type: FormType? = null,
      horizRatio: FormHorizontalRatio = FormHorizontalRatio.RATIO_2, classes: String = "",
      noinline init: (FormBuilder<K>.() -> Unit)? = null
    ): FormBuilder<K> {
      val extendedPanel =
        FormBuilder(
          type,
          horizRatio,
          classes,
          serializer<K>(),
        )
      init?.invoke(extendedPanel)
      return extendedPanel
    }
  }
}

inline fun <reified K : Any> Container.extendedPanel(
  type: FormType? = null,
  horizRatio: FormHorizontalRatio = FormHorizontalRatio.RATIO_2,
  classes: String? = null, className: String? = null,
  noinline init: (FormBuilder<K>.() -> Unit)? = null
): FormBuilder<K> {
  val extendedPanel =
    FormBuilder.create<K>(
      type,
      horizRatio,
      classes ?: className ?: ""
    )
  init?.invoke(extendedPanel)
  this.add(extendedPanel)
  return extendedPanel
}